import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { optimizeImageBlob } from "utils/image-optimize";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  ButtonGroup,
  Flex,
  Text,
  Box,
  useColorModeValue,
} from "@chakra-ui/react";

const BLUR_PIXELS = 14;
const MIN_SIZE = 20;

function getCanvasPoint(canvas, clientX, clientY) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  };
}

function fitContain(containerW, containerH, imgW, imgH) {
  if (!imgW || !imgH || containerW <= 0 || containerH <= 0) return { w: 0, h: 0 };
  const scale = Math.min(containerW / imgW, containerH / imgH);
  return {
    w: Math.floor(imgW * scale),
    h: Math.floor(imgH * scale),
  };
}

export function ImageEditorModal({ isOpen, onClose, imageFile, translationKey = "traffic-violation.form", onSave }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const blurredCanvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const [regions, setRegions] = useState([]);
  const [tool, setTool] = useState("circle");
  const [drawing, setDrawing] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSize, setImageSize] = useState({ w: 0, h: 0 });
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });

  const borderColor = useColorModeValue("gray.300", "gray.600");

  useEffect(() => {
    if (!isOpen || !imageLoaded || imageSize.w <= 0) return;
    const el = containerRef.current;
    if (!el) return;

    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      const w = Math.floor(rect.width);
      const h = Math.floor(rect.height);
      setDisplaySize((prev) => {
        const next = fitContain(w, h, imageSize.w, imageSize.h);
        return prev.w === next.w && prev.h === next.h ? prev : next;
      });
    };

    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, imageLoaded, imageSize.w, imageSize.h]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const blurredCanvas = blurredCanvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img?.complete || !img.naturalWidth) return;

    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    if (!blurredCanvas || regions.length === 0) return;
    const bctx = blurredCanvas.getContext("2d");
    bctx.filter = `blur(${BLUR_PIXELS}px)`;
    bctx.drawImage(img, 0, 0, w, h);
    bctx.filter = "none";

    regions.forEach((r) => {
      ctx.save();
      if (r.type === "circle") {
        ctx.beginPath();
        ctx.arc(r.x, r.y, r.r, 0, Math.PI * 2);
      } else {
        ctx.rect(r.x, r.y, r.w, r.h);
      }
      ctx.clip();
      ctx.drawImage(blurredCanvas, 0, 0, w, h, 0, 0, w, h);
      ctx.restore();
    });
  }, [regions]);

  useEffect(() => {
    if (!isOpen || !imageFile) return;
    setRegions([]);
    setDrawing(null);
    setImageLoaded(false);
    const url = URL.createObjectURL(imageFile);
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setImageSize({ w: img.naturalWidth, h: img.naturalHeight });
      setImageLoaded(true);
    };
    img.src = url;
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [isOpen, imageFile]);

  useEffect(() => {
    if (!imageLoaded || imageSize.w <= 0) return;
    const canvas = canvasRef.current;
    const blurCanvas = blurredCanvasRef.current;
    if (!canvas || !blurCanvas) return;
    const { w, h } = imageSize;
    canvas.width = w;
    canvas.height = h;
    blurCanvas.width = w;
    blurCanvas.height = h;
    redraw();
  }, [imageLoaded, imageSize, redraw, displaySize.w, displaySize.h]);

  useEffect(() => {
    redraw();
  }, [drawing, redraw]);

  const handleMouseDown = useCallback(
    (e) => {
      if (!imageLoaded) return;
      const canvas = canvasRef.current;
      const p = getCanvasPoint(canvas, e.clientX, e.clientY);
      setDrawing({ tool, startX: p.x, startY: p.y, currentX: p.x, currentY: p.y });
    },
    [imageLoaded, tool]
  );

  const handleMouseMove = useCallback((e) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    const p = getCanvasPoint(canvas, e.clientX, e.clientY);
    setDrawing((prev) => ({ ...prev, currentX: p.x, currentY: p.y }));
  }, [drawing]);

  const handleMouseUp = useCallback(() => {
    if (!drawing) return;
    const { tool: t, startX, startY, currentX, currentY } = drawing;
    const dx = currentX - startX;
    const dy = currentY - startY;
    if (t === "circle") {
      const r = Math.max(MIN_SIZE, Math.sqrt(dx * dx + dy * dy));
      setRegions((prev) => [...prev, { type: "circle", x: startX, y: startY, r }]);
    } else {
      const w = Math.abs(dx);
      const h = Math.abs(dy);
      if (w >= MIN_SIZE && h >= MIN_SIZE) {
        const x = Math.min(startX, currentX);
        const y = Math.min(startY, currentY);
        setRegions((prev) => [...prev, { type: "rect", x, y, w, h }]);
      }
    }
    setDrawing(null);
  }, [drawing]);

  const handleClearBlurs = useCallback(() => {
    setRegions([]);
    setDrawing(null);
  }, []);

  const handleSave = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        optimizeImageBlob(blob, 1920, 0.92)
          .then((optimized) => {
            onSave(optimized);
            onClose();
          })
          .catch(() => {
            onSave(blob);
            onClose();
          });
      },
      "image/jpeg",
      0.92
    );
  }, [onSave, onClose]);

  const handleClose = useCallback(() => {
    setRegions([]);
    setDrawing(null);
    onClose();
  }, [onClose]);

  const drawPreview = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !drawing) return;
    const ctx = canvas.getContext("2d");
    const { tool: t, startX, startY, currentX, currentY } = drawing;
    ctx.save();
    ctx.strokeStyle = "rgba(255, 0, 0, 0.8)";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    if (t === "circle") {
      const r = Math.max(MIN_SIZE, Math.hypot(currentX - startX, currentY - startY));
      ctx.beginPath();
      ctx.arc(startX, startY, r, 0, Math.PI * 2);
    } else {
      const x = Math.min(startX, currentX);
      const y = Math.min(startY, currentY);
      const w = Math.abs(currentX - startX);
      const h = Math.abs(currentY - startY);
      ctx.strokeRect(x, y, w, h);
    }
    ctx.stroke();
    ctx.restore();
  }, [drawing]);

  useEffect(() => {
    if (!drawing) return;
    redraw();
    drawPreview();
  }, [drawing, redraw, drawPreview]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="full" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        maxW="900px"
        maxH="90vh"
        my="4"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>
          <Text>{t(`${translationKey}.fields.ait-image.editor-title`)}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          flex="1"
          minH="0"
          overflow="hidden"
          display="flex"
          flexDirection="column"
          gap="3"
          pb="2"
        >
          <Flex wrap="wrap" gap="2" align="center" flexShrink={0}>
            <ButtonGroup size="sm" isAttached variant="outline">
              <Button
                colorScheme={tool === "circle" ? "blue" : "gray"}
                onClick={() => setTool("circle")}
              >
                {t(`${translationKey}.fields.ait-image.blur-circle`)}
              </Button>
              <Button
                colorScheme={tool === "rect" ? "blue" : "gray"}
                onClick={() => setTool("rect")}
              >
                {t(`${translationKey}.fields.ait-image.blur-rect`)}
              </Button>
            </ButtonGroup>
            <Button size="sm" variant="outline" onClick={handleClearBlurs} isDisabled={regions.length === 0}>
              {t(`${translationKey}.fields.ait-image.clear-blurs`)}
            </Button>
          </Flex>
          <Box
            ref={containerRef}
            flex="1"
            minH="0"
            minW="0"
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            bg="black"
            onMouseLeave={() => drawing && setDrawing(null)}
          >
            {imageLoaded && displaySize.w > 0 && displaySize.h > 0 && (
              <canvas
                ref={canvasRef}
                width={imageSize.w}
                height={imageSize.h}
                style={{
                  width: `${displaySize.w}px`,
                  height: `${displaySize.h}px`,
                  cursor: "crosshair",
                  display: "block",
                  flexShrink: 0,
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
            )}
            <canvas
              ref={blurredCanvasRef}
              width={imageSize.w}
              height={imageSize.h}
              style={{ display: "none" }}
              aria-hidden
            />
          </Box>
          <Text fontSize="sm" color="gray.500" flexShrink={0} noOfLines={1}>
            {tool === "circle"
              ? t(`${translationKey}.fields.ait-image.editor-hint-circle`)
              : t(`${translationKey}.fields.ait-image.editor-hint-rect`)}
          </Text>
        </ModalBody>
        <ModalFooter flexShrink={0}>
          <Button variant="ghost" onClick={handleClose}>
            {t("system.actions.cancel")}
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            {t(`${translationKey}.fields.ait-image.save-edits`)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
